
import Vue from "vue";
import TempStarsTableTitle from "@/components/shared/TempStarsTableTitle/TempStarsTableTitle.vue";
import { constants } from "@/utils/constants";
import {
  CA_PROVINCES,
  REGION_OPTIONS_GROUPED,
  US_STATES
} from "@/utils/regions";
import {
  dropKnownHeaderRow,
  formatBytes
} from "@/utils/photoAnnounceIdList";
import {
  buildMessageUsersAudience,
  isIdListAudienceBlocked,
  messageUsersAudienceFingerprint
} from "@/utils/messageUsersAudience";
import Papa from "papaparse";
import {
  previewMessageUsersCount,
  sendMessageUsersBroadcast,
  parseMessageUsersIdFile,
  listMessageUsersBroadcasts,
  MessageUsersParseResult,
  MessageUsersBroadcastRow,
  MessageUsersIdListType
} from "@/api";

const ROLE_OPTIONS = [
  { value: 4, label: "Dental Office" },
  { value: 6, label: "Hygienist" },
  { value: 7, label: "Dental Assistant" },
  { value: 8, label: "Admin / Manager" },
  { value: 9, label: "Associate Dentist" }
];

const MAX_ID_LIST_BYTES = 2 * 1024 * 1024;
/** Keep in sync with tempstars-api notificationBroadcast.service thresholds (T2-6151). */
const LARGE_AUDIENCE_THRESHOLD = 5000;
const MAX_BROADCAST_RECIPIENTS = 20000;

function reasonLabel(reason: string): string {
  switch (reason) {
    case "not_a_number":
      return "Not a number";
    case "duplicate":
      return "Duplicate";
    case "unknown_hygienistId":
      return "No such hygienist ID";
    case "unknown_dentistId":
      return "No such dentist ID";
    default:
      return reason;
  }
}

export default Vue.extend({
  name: "MessageUsers",
  components: { TempStarsTableTitle },
  data() {
    return {
      accessChecked: false,
      isDeveloper: false,
      teamMemberName: "",
      title: "",
      message: "",
      selectedRoles: [] as number[],
      selectedProvinces: [] as string[],
      roleOptions: ROLE_OPTIONS,
      regionItems: REGION_OPTIONS_GROUPED,
      idListType: "hygienist" as MessageUsersIdListType,
      idListFile: null as File | null,
      idListResult: null as MessageUsersParseResult | null,
      idListError: "",
      idListParsing: false,
      idListParseToken: 0,
      showInvalid: false,
      previewCount: null as number | null,
      previewLoading: false,
      previewRequestId: 0,
      previewAudienceFingerprint: "",
      formError: "",
      confirmOpen: false,
      confirmAudienceFingerprint: "",
      confirmTyped: "",
      sendLoading: false,
      history: [] as MessageUsersBroadcastRow[],
      historyLoading: false,
      historyLoadingMore: false,
      historyHasMore: false,
      historyLimit: 50,
      historyOffset: 0,
      historyRequestId: 0,
      toastOpen: false,
      toastMessage: "",
      toastColor: "success"
    };
  },
  computed: {
    titleWarn(): boolean {
      return this.title.length >= Math.floor(80 * 0.9);
    },
    messageWarn(): boolean {
      return this.message.length >= Math.floor(500 * 0.9);
    },
    canPreview(): boolean {
      return (
        this.selectedRoles.length > 0 &&
        !isIdListAudienceBlocked({
          idListError: this.idListError,
          idListFile: this.idListFile,
          idListResult: this.idListResult,
          idListParsing: this.idListParsing
        })
      );
    },
    canSend(): boolean {
      return (
        !!String(this.teamMemberName || "").trim() &&
        !!String(this.title || "").trim() &&
        !!String(this.message || "").trim() &&
        this.selectedRoles.length > 0 &&
        !this.previewLoading &&
        this.previewCount !== null &&
        this.previewCount > 0 &&
        this.previewAudienceFingerprint === this.currentAudienceFingerprint &&
        !this.audienceTooLarge &&
        !isIdListAudienceBlocked({
          idListError: this.idListError,
          idListFile: this.idListFile,
          idListResult: this.idListResult,
          idListParsing: this.idListParsing
        })
      );
    },
    currentAudienceFingerprint(): string {
      return messageUsersAudienceFingerprint(this.buildAudience());
    },
    requiresTypedConfirm(): boolean {
      return (this.previewCount || 0) > LARGE_AUDIENCE_THRESHOLD;
    },
    audienceTooLarge(): boolean {
      return (this.previewCount || 0) > MAX_BROADCAST_RECIPIENTS;
    },
    audienceSummaryText(): string {
      const roles = this.selectedRoles
        .map(r => ROLE_OPTIONS.find(o => o.value === r)?.label || String(r))
        .join(", ");
      const provinces =
        this.selectedProvinces.length > 0
          ? this.selectedProvinces.join(", ")
          : "any";
      const idPart =
        this.idListResult && this.idListResult.validCount > 0
          ? ` · ID list (${this.idListType}): ${this.idListResult.validCount}`
          : "";
      return `Roles: ${roles} · Provinces: ${provinces}${idPart}`;
    }
  },
  watch: {
    selectedRoles() {
      this.invalidatePreview();
    },
    selectedProvinces() {
      this.invalidatePreview();
    },
    idListResult() {
      this.invalidatePreview();
    },
    idListFile() {
      this.invalidatePreview();
    },
    idListParsing() {
      this.invalidatePreview();
    },
    idListError() {
      this.invalidatePreview();
    },
    idListType() {
      this.invalidatePreview();
      if (this.idListFile) {
        this.clearIdList();
      }
    }
  },
  created() {
    this.checkAccess();
  },
  methods: {
    formatBytes,
    reasonLabel,
    checkAccess() {
      try {
        const userStr = localStorage.getItem("user");
        if (!userStr) {
          this.isDeveloper = false;
        } else {
          const user = JSON.parse(userStr);
          const role =
            user && user.role !== undefined
              ? parseInt(String(user.role), 10)
              : constants.ADMIN_ROLES.REGULAR;
          this.isDeveloper = role === constants.ADMIN_ROLES.DEVELOPER;
        }
      } catch {
        this.isDeveloper = false;
      }
      this.accessChecked = true;
      if (this.isDeveloper) {
        this.loadHistory();
      }
    },
    buildAudience() {
      return buildMessageUsersAudience({
        roles: this.selectedRoles,
        provinces: this.selectedProvinces,
        idListType: this.idListType,
        idListResult: this.idListResult
      });
    },
    invalidatePreview() {
      this.previewRequestId += 1;
      this.previewCount = null;
      this.previewAudienceFingerprint = "";
      this.previewLoading = false;
      this.confirmOpen = false;
      this.confirmAudienceFingerprint = "";
      this.confirmTyped = "";
    },
    selectAllCanada() {
      const codes = CA_PROVINCES.map(p => p.code);
      this.selectedProvinces = [
        ...new Set([...this.selectedProvinces, ...codes])
      ];
    },
    selectAllUS() {
      const codes = US_STATES.map(p => p.code);
      this.selectedProvinces = [
        ...new Set([...this.selectedProvinces, ...codes])
      ];
    },
    triggerIdListBrowse() {
      (this.$refs.idListInput as HTMLInputElement)?.click();
    },
    onIdListSelected(event: Event) {
      const input = event.target as HTMLInputElement;
      const selected = input.files && input.files[0];
      if (selected) {
        this.parseIdListFile(selected);
      }
    },
    clearIdList() {
      this.idListParseToken += 1;
      this.idListFile = null;
      this.idListResult = null;
      this.idListError = "";
      this.idListParsing = false;
      this.showInvalid = false;
      // Drop any prior preview so Send cannot reuse a broader audience count.
      this.invalidatePreview();
      const input = this.$refs.idListInput as HTMLInputElement;
      if (input) input.value = "";
    },
    extractCsvFirstColumn(file: File): Promise<string[]> {
      return new Promise((resolve, reject) => {
        Papa.parse(file as any, {
          skipEmptyLines: true,
          complete: (res: { data: unknown[] }) => {
            const rows = (res.data as unknown[]).map((r: unknown) =>
              Array.isArray(r) ? r[0] : r
            );
            resolve(
              rows.map((v: unknown) =>
                v === null || v === undefined ? "" : String(v)
              )
            );
          },
          error: (err: unknown) => reject(err)
        });
      });
    },
    async extractXlsxFirstColumn(file: File): Promise<string[]> {
      const XLSX = await import("xlsx");
      const buf = await file.arrayBuffer();
      // Sniff: xlsx is a zip (PK..) — do not trust MIME alone.
      const bytes = new Uint8Array(buf.slice(0, 4));
      const isZip =
        bytes[0] === 0x50 &&
        bytes[1] === 0x4b &&
        (bytes[2] === 0x03 || bytes[2] === 0x05 || bytes[2] === 0x07);
      if (!isZip) {
        throw new Error("File does not look like a valid .xlsx (bad file signature).");
      }
      const wb = XLSX.read(buf, { type: "array" });
      const firstSheet = wb.Sheets[wb.SheetNames[0]];
      const aoa = XLSX.utils.sheet_to_json(firstSheet, {
        header: 1,
        blankrows: false
      }) as unknown[][];
      return aoa.map((r: unknown[]) => {
        const v = Array.isArray(r) ? r[0] : r;
        return v === null || v === undefined ? "" : String(v);
      });
    },
    async parseIdListFile(file: File) {
      this.invalidatePreview();
      this.idListError = "";
      this.idListResult = null;
      if (file.size > MAX_ID_LIST_BYTES) {
        this.clearIdList();
        this.idListError = "File is too large. Maximum size is 2 MB.";
        return;
      }
      const name = file.name.toLowerCase();
      const isCsv = name.endsWith(".csv");
      const isXlsx = name.endsWith(".xlsx");
      if (!isCsv && !isXlsx) {
        this.clearIdList();
        this.idListError = "Please upload a .xlsx or .csv file.";
        return;
      }
      this.idListFile = file;
      this.idListParsing = true;
      const token = (this.idListParseToken += 1);
      const idTypeAtParse = this.idListType;
      try {
        const raw = isCsv
          ? await this.extractCsvFirstColumn(file)
          : await this.extractXlsxFirstColumn(file);
        const rows = dropKnownHeaderRow(raw);
        const result = await parseMessageUsersIdFile(idTypeAtParse, rows);
        if (token !== this.idListParseToken) return;
        this.idListResult = result;
        if (result.validCount === 0) {
          this.idListError =
            "No valid IDs were found in this file. Please check the file and try again.";
          this.previewCount = null;
        }
      } catch (e) {
        if (token !== this.idListParseToken) return;
        this.idListError =
          e instanceof Error ? e.message : "Could not read this file.";
        this.idListResult = null;
        this.previewCount = null;
        this.confirmOpen = false;
        this.confirmTyped = "";
      } finally {
        if (token === this.idListParseToken) {
          this.idListParsing = false;
        }
      }
    },
    async runPreview() {
      if (!this.canPreview) return;
      this.invalidatePreview();
      this.formError = "";
      const audience = this.buildAudience();
      const fingerprint = messageUsersAudienceFingerprint(audience);
      const requestId = this.previewRequestId;
      this.previewLoading = true;
      try {
        const result = await previewMessageUsersCount(audience);
        if (
          requestId !== this.previewRequestId ||
          fingerprint !== this.currentAudienceFingerprint ||
          !this.canPreview
        ) return;
        this.previewCount = result.recipientCount;
        this.previewAudienceFingerprint = fingerprint;
        if (result.recipientCount === 0) {
          this.formError = "No recipients match the selected audience.";
        } else if (result.recipientCount > MAX_BROADCAST_RECIPIENTS) {
          this.formError =
            `Audience has ${result.recipientCount} recipients (max ${MAX_BROADCAST_RECIPIENTS}). Narrow roles/provinces or use an ID list.`;
        }
      } catch (e) {
        if (
          requestId !== this.previewRequestId ||
          fingerprint !== this.currentAudienceFingerprint
        ) return;
        this.previewCount = null;
        this.previewAudienceFingerprint = "";
        this.formError =
          e instanceof Error ? e.message : "Preview failed.";
      } finally {
        if (requestId === this.previewRequestId) this.previewLoading = false;
      }
    },
    openConfirm() {
      if (!this.canSend) return;
      this.confirmTyped = "";
      this.confirmAudienceFingerprint = this.currentAudienceFingerprint;
      this.confirmOpen = true;
    },
    async confirmSend() {
      if (this.sendLoading) return;
      if (
        !this.confirmOpen ||
        !this.canSend ||
        this.confirmAudienceFingerprint !== this.currentAudienceFingerprint ||
        (this.requiresTypedConfirm && this.confirmTyped !== "SEND")
      ) {
        this.invalidatePreview();
        this.formError = "Audience changed or its preview is no longer valid. Preview again before sending.";
        return;
      }
      const audience = this.buildAudience();
      this.sendLoading = true;
      this.formError = "";
      try {
        const result = await sendMessageUsersBroadcast({
          teamMemberName: String(this.teamMemberName).trim(),
          title: String(this.title).trim(),
          message: String(this.message).trim(),
          audience,
          confirm: this.requiresTypedConfirm ? true : undefined
        });
        this.toastMessage = `Sent to ${result.recipientCount} users.`;
        this.toastColor = "success";
        this.toastOpen = true;
        this.confirmOpen = false;
        this.clearComposer();
        await this.loadHistory(true);
      } catch (e) {
        this.formError =
          e instanceof Error ? e.message : "Send failed.";
        this.toastMessage = this.formError;
        this.toastColor = "error";
        this.toastOpen = true;
      } finally {
        this.sendLoading = false;
      }
    },
    clearComposer() {
      this.teamMemberName = "";
      this.title = "";
      this.message = "";
      this.selectedRoles = [];
      this.selectedProvinces = [];
      this.clearIdList();
      this.previewCount = null;
      this.formError = "";
    },
    copyToComposer(item: MessageUsersBroadcastRow) {
      this.title = item.title || "";
      this.message = item.message || "";
      this.teamMemberName = item.teamMemberName || "";
      const filters = item.audienceFilters || ({} as any);
      this.selectedRoles = Array.isArray(filters.roles) ? [...filters.roles] : [];
      this.selectedProvinces = Array.isArray(filters.provinces)
        ? [...filters.provinces]
        : [];
      this.clearIdList();
      this.previewCount = null;
      const hadIdList = !!(filters.idList && Array.isArray(filters.idList.ids) && filters.idList.ids.length);
      this.toastMessage = hadIdList
        ? "Copied draft. Re-upload the ID list before sending — it was not restored."
        : "Copied into composer as a new draft.";
      this.toastColor = hadIdList ? "warning" : "info";
      this.toastOpen = true;
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    formatHistoryDate(value: string): string {
      if (!value) return "";
      try {
        return new Date(value).toLocaleString();
      } catch {
        return String(value);
      }
    },
    async loadHistory(reset = true) {
      if (
        !reset &&
        (!this.historyHasMore || this.historyLoading || this.historyLoadingMore)
      ) {
        return;
      }
      const requestId = ++this.historyRequestId;
      const offset = reset ? 0 : this.historyOffset + this.historyLimit;
      if (reset) {
        this.historyLoading = true;
        this.historyLoadingMore = false;
      } else {
        this.historyLoadingMore = true;
      }
      try {
        const result = await listMessageUsersBroadcasts(
          this.historyLimit,
          offset
        );
        if (requestId !== this.historyRequestId) return;
        const rows = result.data || [];
        this.history = reset ? rows : [...this.history, ...rows];
        this.historyHasMore = rows.length === this.historyLimit;
        // Keep the last successful cursor when a request fails so retry cannot skip rows.
        this.historyOffset = offset;
      } catch (e) {
        if (requestId !== this.historyRequestId) return;
        this.toastMessage =
          e instanceof Error ? e.message : "Could not load history.";
        this.toastColor = "error";
        this.toastOpen = true;
      } finally {
        if (requestId === this.historyRequestId) {
          this.historyLoading = false;
          this.historyLoadingMore = false;
        }
      }
    },
    async loadMoreHistory() {
      await this.loadHistory(false);
    }
  }
});
